import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { registerSW } from 'virtual:pwa-register';
import { App } from './App.js';
import { ErrorBoundary } from './components/shared/ErrorBoundary.js';
import './styles/global.css';
import './styles/terminal.css';
import './styles/layout.css';
import './styles/history.css';
import './styles/admin.css';
import './styles/gate.css';

/** דף שנפתח לפני פחות מזה — אין בו מה לאבד, וטוענים גרסה חדשה מיד (בלי שהמשתמש יצטרך לפתוח את האפליקציה פעמיים) */
const FRESH_PAGE_MS = 20_000;
const UPDATE_CHECK_MS = 30 * 60 * 1000;

registerSW({
  immediate: true,
  // טלפונים משאירים את האפליקציה פתוחה ימים: בודקים אם יצאה גרסה חדשה מדי חצי שעה ובכל חזרה אליה
  onRegisteredSW: (_swUrl, registration) => {
    if (!registration) return;
    const check = () => {
      registration.update().catch(() => undefined);
    };
    window.setInterval(check, UPDATE_CHECK_MS);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') check();
    });
  },
  // גרסה חדשה הופעלה. טעינה מחדש באמצע שימוש מאפסת את המסך (הטרמינל, מה שהוקלד, הגלילה). לכן: בדף שנפתח
  // הרגע או כשהדף לא נראה — טוענים מיד; באמצע שימוש מופיע פס "גרסה חדשה מוכנה — עדכן עכשיו", והטעינה
  // קורית מעצמה ברגע שהמסך נעלם (מעבר ללשונית אחרת, נעילת מסך, חזרה למסך הבית).
  onNeedReload: () => {
    if (performance.now() < FRESH_PAGE_MS || document.visibilityState === 'hidden') {
      window.location.reload();
      return;
    }
    window.dispatchEvent(new CustomEvent('pmr-update-ready'));
    const reloadWhenHidden = () => {
      if (document.visibilityState !== 'hidden') return;
      document.removeEventListener('visibilitychange', reloadWhenHidden);
      window.location.reload();
    };
    document.addEventListener('visibilitychange', reloadWhenHidden);
  },
});

// אחרי פריסה של גרסה חדשה, קובץ קוד ישן (למשל אזור הניהול או המפה) כבר לא קיים בשרת — טעינה מחדש
// אחת מביאה את הגרסה העדכנית. מוגבל לפעם אחת לכל לשונית כדי לא להיכנס ללולאת ריענון אם הבעיה אמיתית.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  try {
    if (sessionStorage.getItem('pmr-preload-reloaded') === '1') return;
    sessionStorage.setItem('pmr-preload-reloaded', '1');
  } catch {
    // בלי sessionStorage — עדיף לא לטעון מחדש מאשר לסכן לולאה
    return;
  }
  window.location.reload();
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('root element not found');

createRoot(rootEl).render(
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>,
);
